/**
 * UI Store for Atom Assist
 *
 * Manages UI-specific state that should persist across sessions:
 * - Sidebar state (open/closed, width, tab selection)
 * - Right sidebar state (artifact viewer, source viewer)
 * - Layout preferences
 * - User preferences
 *
 * This state persists in localStorage since it represents user preferences
 * that should survive page reloads and browser sessions.
 *
 * @see /home/nuclearn/frontend/.github/copilot-instructions.md
 */

import { createJSONStorage, persist } from "zustand/middleware";

import { create } from "zustand";

// ===========================
// Types
// ===========================

export interface SidebarState {
  isOpen: boolean;
  width: number;
  tabValue: "historyView" | "datasetSearch" | "files";
}

export interface RightSidebarState {
  isOpen: boolean;
  width: number;
  selectedItem: any | null;
  isDetailedSourceViewOpen: boolean;
  isArtifactItemOpen: boolean;
  currentArtifactId: string | null;
}

export interface LayoutState {
  showTheLayoutSidebar: boolean;
}

export interface PreferencesState {
  disableRehype: boolean;
  /** Whether to auto-open sidebar when artifacts/sources appear (default: true) */
  autoOpenSidebar: boolean;
}

// ===========================
// UI Store State Interface
// ===========================

export interface UIStoreState {
  // ========== Sidebar State ==========
  sidebar: SidebarState;

  // ========== Right Sidebar State ==========
  rightSidebar: RightSidebarState;

  /**
   * 1-based citation number whose source card should be scrolled into view.
   * CitationLink sets it on click; SourcesChunkView consumes it via an
   * effect that scrolls the matching `source-N` element after layout, then
   * clears it. Replaces the old setTimeout-poll loop in scrollToSource.
   */
  scrollTargetSource: number | null;

  // ========== Layout State ==========
  layout: LayoutState;

  // ========== Preferences ==========
  preferences: PreferencesState;

  // ========== Actions: Sidebar ==========
  setSidebar: (updates: Partial<SidebarState>) => void;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  setSidebarWidth: (width: number) => void;
  setSidebarTab: (tabValue: SidebarState["tabValue"]) => void;

  // ========== Actions: Right Sidebar ==========
  setRightSidebar: (updates: Partial<RightSidebarState>) => void;
  toggleRightSidebar: () => void;
  setScrollTargetSource: (n: number | null) => void;
  setRightSidebarWidth: (width: number) => void;
  openArtifact: (artifactId: string) => void;
  closeArtifact: () => void;
  openDetailedSourceView: (item: any) => void;
  closeDetailedSourceView: () => void;

  // ========== Actions: Layout ==========
  setLayout: (updates: Partial<LayoutState>) => void;
  toggleLayoutSidebar: () => void;

  // ========== Actions: Preferences ==========
  setPreferences: (updates: Partial<PreferencesState>) => void;
  toggleDisableRehype: () => void;
  toggleAutoOpenSidebar: () => void;

  // ========== Actions: Reset ==========
  resetUI: () => void;
}

// ===========================
// Initial State
// ===========================

const getInitialState = () => ({
  sidebar: {
    isOpen: false,
    width: 450,
    tabValue: "historyView" as const,
  },
  rightSidebar: {
    isOpen: false,
    width: 400,
    selectedItem: null,
    isDetailedSourceViewOpen: false,
    isArtifactItemOpen: false,
    currentArtifactId: null,
  },
  scrollTargetSource: null as number | null,
  layout: {
    showTheLayoutSidebar: true,
  },
  preferences: {
    disableRehype: false,
    autoOpenSidebar: true,
  },
});

// ===========================
// Store Implementation
// ===========================

export const useUIStore = create<UIStoreState>()(
  persist(
    (set) => ({
      // Initialize state
      ...getInitialState(),

      // ========== Actions: Sidebar ==========

      setSidebar: (updates) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            ...updates,
          },
        }));
      },

      toggleSidebar: () => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen: !state.sidebar.isOpen,
          },
        }));
      },

      openSidebar: () => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen: true,
          },
        }));
      },

      closeSidebar: () => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen: false,
          },
        }));
      },

      setSidebarWidth: (width) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            width,
          },
        }));
      },

      setSidebarTab: (tabValue) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            tabValue,
          },
        }));
      },

      // ========== Actions: Right Sidebar ==========

      setRightSidebar: (updates) => {
        set((state) => ({
          rightSidebar: {
            ...state.rightSidebar,
            ...updates,
          },
        }));
      },

      toggleRightSidebar: () => {
        set((state) => ({
          rightSidebar: {
            ...state.rightSidebar,
            isOpen: !state.rightSidebar.isOpen,
          },
        }));
      },

      setScrollTargetSource: (n) => {
        set({ scrollTargetSource: n });
      },

      setRightSidebarWidth: (width) => {
        set((state) => ({
          rightSidebar: {
            ...state.rightSidebar,
            width,
          },
        }));
      },

      openArtifact: (artifactId) => {
        set((state) => ({
          rightSidebar: {
            ...state.rightSidebar,
            isOpen: true,
            selectedItem: "artifacts",
            isArtifactItemOpen: true,
            currentArtifactId: artifactId,
          },
        }));
      },

      closeArtifact: () => {
        set((state) => ({
          rightSidebar: {
            ...state.rightSidebar,
            isArtifactItemOpen: false,
            currentArtifactId: null,
          },
        }));
      },

      openDetailedSourceView: (item) => {
        set((state) => ({
          rightSidebar: {
            ...state.rightSidebar,
            isOpen: true,
            isDetailedSourceViewOpen: true,
            selectedItem: item,
          },
        }));
      },

      closeDetailedSourceView: () => {
        set((state) => ({
          rightSidebar: {
            ...state.rightSidebar,
            isDetailedSourceViewOpen: false,
            selectedItem: null,
          },
        }));
      },

      // ========== Actions: Layout ==========

      setLayout: (updates) => {
        set((state) => ({
          layout: {
            ...state.layout,
            ...updates,
          },
        }));
      },

      toggleLayoutSidebar: () => {
        set((state) => ({
          layout: {
            ...state.layout,
            showTheLayoutSidebar: !state.layout.showTheLayoutSidebar,
          },
        }));
      },

      // ========== Actions: Preferences ==========

      setPreferences: (updates) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...updates,
          },
        }));
      },

      toggleDisableRehype: () => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            disableRehype: !state.preferences.disableRehype,
          },
        }));
      },

      toggleAutoOpenSidebar: () => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            autoOpenSidebar: !state.preferences.autoOpenSidebar,
          },
        }));
      },

      // ========== Actions: Reset ==========

      resetUI: () => {
        set(getInitialState());
      },
    }),
    {
      name: "atom-assist-ui",
      storage: createJSONStorage(() => localStorage),
      // Persist everything except transient signals like scrollTargetSource.
      partialize: (state) => {
        const { scrollTargetSource: _drop, ...rest } = state as UIStoreState & {
          scrollTargetSource: number | null;
        };
        return rest;
      },
    },
  ),
);
